<template>
  <PageLayout>
    <template v-slot:header>
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <b-button
          :title="labels.create"
          variant="tertiary"
          class="medium btn-user-attributes-create"
          @click="showNew()"
        >
          <i class="material-icons">add_circle</i>
          {{ labels.create }}
        </b-button>
      </div>
      <MainNavbar />
    </template>
    <template v-slot:main>
      <b-card class="without-padding-bottom">
        <header slot="header">
          <b-row>
            <b-col lg="12">
              {{ labels.title }}
            </b-col>
            <b-col lg="12" class="text-gray03 pl-2 pt-3">
              <h6 v-if="!loading">
                {{ labels.subtitle | parse({ COUNT: attributesSize }) }}
              </h6>
            </b-col>
          </b-row>
        </header>

        <div v-if="loading" class="loading">
          <loading />
          <p>{{ labels.loading }}</p>
        </div>

        <div v-else>
          <div v-if="userAttributes.length === 0" class="loading">
            <p>{{ labels.none }}</p>
          </div>

          <div class="full-table-wraper" v-else>
            <b-table
              responsive
              striped
              :fields="fields"
              :items="userAttributes"
              class="table-responsive"
            >
              <template v-slot:cell(label)="data">
                <b-row class="padding-row">
                  <b-col class="float-right p-2">
                    <b-row>
                      <b-col lg="12" class="user-attributes-labels">
                        <div
                          v-for="language in Object.keys(data.item.label)"
                          :key="`${data.item.id}-${language}`"
                          class="user-attributes-label"
                        >
                          <span class="medium fullname">
                            <img
                              :src="`/icons/languages/${language}.svg`"
                              class="language-icon ml-2 mr-2"
                            />
                            {{ data.item.label[language] }}
                          </span>
                        </div>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </template>
              <template v-slot:cell(actions)="data">
                <div class="flexbox entity">
                  <i
                    :title="labels.edit"
                    class="material-icons mt-2 text-primary h5 mr-2"
                    @click="showDetail(data.item)"
                  >
                    edit
                  </i>
                  <i
                    :title="labels.remove"
                    class="material-icons mt-2 text-danger h5"
                    @click="removeUserAttribute(data.item)"
                  >
                    delete
                  </i>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </b-card>

      <b-modal
        ref="remove-user-attribute"
        :title="
          selectedUserAttribute &&
            selectedUserAttribute.label[
              Object.keys(selectedUserAttribute.label)[0]
            ]
        "
        :ok-title="resources.general.buttons.yes"
        ok-variant="primary"
        :cancel-title="resources.general.buttons.no"
        @ok="doRemoveUserAttribute"
        cancel-variant="white"
        button-size="large"
      >
        <p>{{ labels.removeMessage }}</p>
      </b-modal>

      <div class="mb-4 overflow-auto">
        <div class="mt-3" v-if="!loading && numberOfPages > 1">
          <b-pagination-nav
            :link-gen="linkGen"
            :number-of-pages="numberOfPages"
            align="center"
            use-router
            v-model="page"
          >
          </b-pagination-nav>
        </div>
      </div>

      <modal-plans
        ref="component-modal-plans"
        :title="resources.general.menu.userAttributes"
        :hideHeaderClose="true"
        :visible="true"
        v-if="!$can('use', 'userAttribute') || isBasicPlan"
        @hide="handleHide"
      />
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import Loading from '@/components/Shared/Loading.vue'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import { HIGH } from '@/helpers/messages-priority'
import { isBasicPlan } from '@/service/user'
import MainNavbar from '@/components/Management/MainNavbar'

export default {
  components: {
    Breadcrumb,
    PageLayout,
    Loading,
    ModalPlans,
    MainNavbar,
  },

  data() {
    return {
      selectedUserAttribute: null,
      page: 1,
      limit: 10,
      offset: 0,
    }
  },

  created() {
    this.loadAttributes()
  },

  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'user-attributes' })
  },

  watch: {
    page() {
      this.offset = (this.page - 1) * this.limit
      this.loadAttributes()
    },
  },

  computed: {
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.settings,
        },
      ]
    },

    fields: function() {
      return [
        {
          key: 'label',
          label: this.labels.labelAttributes,
        },
        {
          key: 'actions',
          label: this.labels.labelActions,
        },
      ]
    },

    ...mapGetters({
      resources: 'resources',
      loading: 'userAttributes/listLoading',
      userAttributes: 'userAttributes/listUserAttributes',
      attributesSize: 'userAttributes/listTotal',
    }),

    labels() {
      return this.resources.page.home.userAttributes
    },

    numberOfPages() {
      const numberOfPages = this.attributesSize / this.limit
      if (numberOfPages > 0 && Number.isInteger(numberOfPages)) {
        return numberOfPages
      }
      return parseInt(numberOfPages) + 1
    },

    isBasicPlan() {
      return isBasicPlan()
    },
  },

  methods: {
    handleHide() {
      this.$router.push({ name: 'dashboard' })
    },

    showNew() {
      this.$router.push({
        name: 'user-attributes.detail',
        params: { id: 'new' },
      })
    },

    showDetail(userAttribute) {
      this.$router.push({
        name: 'user-attributes.detail',
        params: { id: userAttribute.id },
      })
    },

    removeUserAttribute(userAttribute) {
      this.selectedUserAttribute = userAttribute
      this.$refs['remove-user-attribute'].show()
    },

    doRemoveUserAttribute() {
      if (this.selectedUserAttribute) {
        const doRemoveUserAttributePromise = this.$store.dispatch(
          'userAttributes/removeUserAttribute',
          this.selectedUserAttribute
        )
        doRemoveUserAttributePromise
          .then(() => {
            this.$root.$emit('app:message:show', {
              priority: HIGH,
              text: this.labels.removeSuccessMessage,
              status: 'success',
            })
            this.loadAttributes()
          })
          .catch(() => {
            this.$root.$emit('app:message:show', {
              priority: HIGH,
              text: this.resources.general.errors.response500,
              status: 'danger',
            })
          })
        return doRemoveUserAttributePromise
      }
    },

    loadAttributes() {
      this.$store.dispatch('userAttributes/loadUserAttributesList', {
        offset: this.offset,
        limit: this.limit,
      })
    },

    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';
@import '~@/styles/custom/variables';

.btn-user-attributes-create {
  border-radius: 10px !important;
  height: $btn-height;
  padding: 0.7rem 1rem 0.5rem;

  i {
    display: inline-block;
    font-size: 16px;
    line-height: 16px;
    vertical-align: text-top;
  }
}

.loading {
  text-align: center;
  width: 100%;
  margin-top: 10%;
  margin-bottom: 10%;
  margin-left: 0.9px;
  margin-right: 0.9px;
}

.without-padding-bottom {
  margin-bottom: 20px;
}

.card-header {
  padding-right: 15px;
}

.header-text {
  font-size: 12px;
}

table-striped tbody tr:nth-of-type(odd) {
  background-color: #f6f7fd !important;
}

.flexbox {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &.entity {
    justify-content: center;
  }
}

.padding-row {
  .row {
    display: block !important;
  }
}

.ellipsis-text {
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis;

  &.min-width {
    width: 10px;
  }

  &.user-attributes-values {
    // max-width: 550px;
  }
}

.full-table-wraper {
  margin-left: -20px;
  margin-right: -20px;

  /deep/ .b-table {
    thead {
      text-align: center;

      th {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
    td {
      &.action-col {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }
}

.action-description {
  font-size: 0.7rem;
  font-weight: bold;
}

.action-description-detail {
  font-size: 1.1rem;
  font-weight: bold;
}

.body-card {
  width: 50%;
}

.card-header {
  font-size: 24px;
}

.user-attributes-labels {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  .user-attributes-label {
    .language-icon {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
